import React, { useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { Upload, Printer } from 'react-feather';
import { DatePicker, DatePickerInput } from 'carbon-components-react';
import fileDownload from 'js-file-download';

import MainHeader from '../../../components/main-header/main-header.component';
import Mainmenu from '../../../components/menu/main-menu/main-menu.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import Footer from '../../../components/footer/footer.component';

import MainLoaderModal from '../../../components/modal/main-loader/main-loader.component';
import { selectCurrentUser } from '../../../redux/selectors/user.selectors';

import TrialBalancePrintModal from '../../../components/modal/trial-balance/trial-balance.component';
import { ExpTrialBalance } from '../../../components/expandable/expandable-pane/pane.component';

import './trial-balance.styles.scss';
import { selectTrialBalanceReport } from '../../../redux/analytics/analytics.selectors';
import { formatCurrency } from '../../../utils/common.util';
import { fetchService } from '../../../api/common';

const TrialBalanceReportPage = ({
  trialBalanceReport: { fetching, fetchError, data },
  fetchTrialBalanceReport,
  currentUser: { authToken, school },
}) => {
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openLoaderModal, setOpenLoaderModal] = useState(fetching);

  const [openExportLoaderModal, setOpenExportLoaderModal] = useState(false);
  const [exportLoaderModalErrorMessage, setExportLoaderModalErrorMessage] =
    useState(null);

  const exportTrialBalanceReport = async () => {
    setExportLoaderModalErrorMessage(null);
    setOpenExportLoaderModal(true);

    const response = await fetchService(
      `analytics/trial-balance-report/export?dateTo=${dateTo}&dateFrom=${dateFrom}`,
      authToken,
    );

    if (response.success) {
      setOpenExportLoaderModal(false);
      fileDownload(
        response.data.data,
        `trial-balance-report-${new Date()}.xlsx`,
      );

      return;
    }

    setExportLoaderModalErrorMessage(response.message);
  };

  return (
    <div>
      <MainHeader />

      <MainLoaderModal
        openModal={openExportLoaderModal}
        description="Exporting Trial Balance Report"
        errorMessage={exportLoaderModalErrorMessage}
        tryAgainAction={exportTrialBalanceReport}
        cancelAction={() => setOpenExportLoaderModal(false)}
      />

      <MainLoaderModal
        openModal={
          (openLoaderModal && fetching) || (openLoaderModal && fetchError)
        }
        loading={fetching}
        description={'Generating trial balance report...'}
        errorMessage={fetchError}
        tryAgainAction={() => {
          setOpenLoaderModal(true);
          fetchTrialBalanceReport(dateFrom, dateTo, authToken);
        }}
        cancelAction={() => setOpenLoaderModal(false)}
      />

      <TrialBalancePrintModal
        open={openPrintModal}
        school={school}
        data={data}
        cancelAction={() => setOpenPrintModal(false)}
      />

      <span className="right">
        <Mainmenu />
      </span>

      <div className="balance-sheet-page page-container">
        <div>
          <p>
            <span className="text-12 font-bold text-grey">You are here</span>
            &nbsp;&nbsp;
            <Link to="/analytics" className="text-12 text-dark">
              Analytics
            </Link>
            &nbsp;<span className="text-12">&gt;</span>&nbsp;
            <span className="text-12 text-primary font-bold">
              Trial Balance
            </span>
          </p>
        </div>

        <hr className="grey-hr mt-20" />

        <div className="h-24 uppercase mt-15">Trial Balance</div>

        <div className="mt-20">
          <div className="bx--row">
            <div className="bx--col-lg-3">
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                id="start-date-picker"
                onClose={(value) => {
                  if (value.length > 0) {
                    setDateFrom(
                      [
                        value[0].getFullYear(),
                        ('0' + (value[0].getMonth() + 1)).slice(-2),
                        ('0' + value[0].getDate()).slice(-2),
                      ].join('-'),
                    );
                  }
                }}>
                <DatePickerInput
                  className="start-date"
                  id="start-date"
                  name="startDate"
                  labelText="Date from"
                  value={dateFrom}
                  pattern="d{1,2}/d{4}"
                  placeholder="YYYY-MM-DD"
                  type="text"
                />
              </DatePicker>
            </div>
            <div className="bx--col-lg-3 npl npr">
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                id="end-date-picker"
                onClose={(value) => {
                  if (value.length > 0) {
                    setDateTo(
                      [
                        value[0].getFullYear(),
                        ('0' + (value[0].getMonth() + 1)).slice(-2),
                        ('0' + value[0].getDate()).slice(-2),
                      ].join('-'),
                    );
                  }
                }}>
                <DatePickerInput
                  className="end-date"
                  id="end-date"
                  name="endDate"
                  labelText="Date to"
                  value={dateTo}
                  pattern="d{1,2}/d{4}"
                  placeholder="YYYY-MM-DD"
                  type="text"
                />
              </DatePicker>
            </div>
            <div className="bx--col-lg-2">
              <CustomButton
                classes="f-width primary mt-25 font-bold"
                disabled={dateFrom.length === 0 || dateTo.length === 0}
                onClick={() => {
                  setOpenLoaderModal(true);
                  fetchTrialBalanceReport(dateFrom, dateTo, authToken);
                }}>
                RUN REPORT
              </CustomButton>
            </div>
          </div>
        </div>

        <div className="mt-30">
          <div>
            <ExpTrialBalance
              creditLabel="- Credit"
              headerData={{
                title: 'Fixed Assets',
                opening: data
                  ? formatCurrency(data.assets.fixedAssets.opening)
                  : 'N/A',
                closing: data
                  ? formatCurrency(data.assets.fixedAssets.closing)
                  : 'N/A',
                debit: data
                  ? formatCurrency(data.assets.fixedAssets.debit)
                  : 'N/A',
                credit: data
                  ? `${formatCurrency(data.assets.fixedAssets.credit)}`
                  : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.fixedAssets.ledgerCategories.map((ledger) => (
                      <tr>
                        <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                        <td>{ledger.description}</td>
                        <td>{formatCurrency(ledger.opening)}</td>
                        <td>{formatCurrency(ledger.debit)}</td>
                        <td>{formatCurrency(ledger.credit)}</td>
                        <td>
                          <span className="right">
                            {formatCurrency(ledger.closing)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>

            <ExpTrialBalance
              creditLabel="- Credit"
              headerData={{
                title: 'Current Assets',
                opening: data
                  ? formatCurrency(data.assets.currentAssets.opening)
                  : 'N/A',
                closing: data
                  ? formatCurrency(data.assets.currentAssets.closing)
                  : 'N/A',
                debit: data
                  ? formatCurrency(data.assets.currentAssets.debit)
                  : 'N/A',
                credit: data
                  ? `${formatCurrency(data.assets.currentAssets.credit)}`
                  : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.currentAssets.ledgerCategories.map(
                      (ledger) => (
                        <tr>
                          <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                          <td>{ledger.description}</td>
                          <td>{formatCurrency(ledger.opening)}</td>
                          <td>{formatCurrency(ledger.debit)}</td>
                          <td>{formatCurrency(ledger.credit)}</td>
                          <td>
                            <span className="right">
                              {formatCurrency(ledger.closing)}
                            </span>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>

            <ExpTrialBalance
              debitLabel="- Debit"
              headerData={{
                title: 'Short Term Liabilities',
                opening: data
                  ? formatCurrency(
                      data.liabilities.shortTermLiabilities.opening,
                    )
                  : 'N/A',
                closing: data
                  ? formatCurrency(
                      data.liabilities.shortTermLiabilities.closing,
                    )
                  : 'N/A',
                credit: data
                  ? formatCurrency(data.liabilities.shortTermLiabilities.credit)
                  : 'N/A',
                debit: data
                  ? `${formatCurrency(
                      data.liabilities.shortTermLiabilities.debit,
                    )}`
                  : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.liabilities.shortTermLiabilities.ledgerCategories.map(
                      (ledger) => (
                        <tr>
                          <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                          <td>{ledger.description}</td>
                          <td>{formatCurrency(ledger.opening)}</td>
                          <td>{formatCurrency(ledger.debit)}</td>
                          <td>{formatCurrency(ledger.credit)}</td>
                          <td>
                            <span className="right">
                              {formatCurrency(ledger.closing)}
                            </span>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>

            <ExpTrialBalance
              debitLabel="- Debit"
              headerData={{
                title: 'Long Term Liabilities',
                opening: data
                  ? formatCurrency(data.liabilities.longTermLiabilities.opening)
                  : 'N/A',
                closing: data
                  ? formatCurrency(data.liabilities.longTermLiabilities.closing)
                  : 'N/A',
                credit: data
                  ? formatCurrency(data.liabilities.longTermLiabilities.credit)
                  : 'N/A',
                debit: data
                  ? `${formatCurrency(
                      data.liabilities.longTermLiabilities.debit,
                    )}`
                  : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.liabilities.longTermLiabilities.ledgerCategories.map(
                      (ledger) => (
                        <tr>
                          <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                          <td>{ledger.description}</td>
                          <td>{formatCurrency(ledger.opening)}</td>
                          <td>{formatCurrency(ledger.debit)}</td>
                          <td>{formatCurrency(ledger.credit)}</td>
                          <td>
                            <span className="right">
                              {formatCurrency(ledger.closing)}
                            </span>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>

            <ExpTrialBalance
              debitLabel="- Debit"
              headerData={{
                title: 'Capital & Reserves',
                opening: data
                  ? formatCurrency(
                      data.equity.capital.opening +
                        data.equity.reserves.opening,
                    )
                  : 'N/A',
                closing: data
                  ? formatCurrency(
                      data.equity.capital.closing +
                        data.equity.reserves.closing,
                    )
                  : 'N/A',
                credit: data
                  ? formatCurrency(
                      data.equity.capital.credit + data.equity.reserves.credit,
                    )
                  : 'N/A',
                debit: data
                  ? `${formatCurrency(
                      data.equity.capital.debit + data.equity.reserves.debit,
                    )}`
                  : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ...data.equity.capital.ledgerCategories,
                      ...data.equity.reserves.ledgerCategories,
                    ].map((ledger) => (
                      <tr>
                        <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                        <td>{ledger.description}</td>
                        <td>{formatCurrency(ledger.opening)}</td>
                        <td>{formatCurrency(ledger.debit)}</td>
                        <td>{formatCurrency(ledger.credit)}</td>
                        <td>
                          <span className="right">
                            {formatCurrency(ledger.closing)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>

            <ExpTrialBalance
              debitLabel="- Debit"
              headerData={{
                title: 'Revenue',
                opening: data ? formatCurrency(data.revenue.opening) : 'N/A',
                closing: data ? formatCurrency(data.revenue.closing) : 'N/A',
                credit: data ? formatCurrency(data.revenue.credit) : 'N/A',
                debit: data ? formatCurrency(data.revenue.debit) : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.revenue.ledgers.map((ledger) => (
                      <tr>
                        <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                        <td>{ledger.description}</td>
                        <td>{formatCurrency(ledger.opening)}</td>
                        <td>{formatCurrency(ledger.debit)}</td>
                        <td>{formatCurrency(ledger.credit)}</td>
                        <td>
                          <span className="right">
                            {formatCurrency(ledger.closing)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>
            <ExpTrialBalance
              creditLabel="- Credit"
              headerData={{
                title: 'Expenditure',
                opening: data ? formatCurrency(data.expenses.opening) : 'N/A',
                closing: data ? formatCurrency(data.expenses.closing) : 'N/A',
                debit: data ? formatCurrency(data.expenses.debit) : 'N/A',
                credit: data ? formatCurrency(data.expenses.credit) : 'N/A',
              }}>
              {data && (
                <table className="bx--data-table table-transparent">
                  <thead>
                    <tr>
                      <th>
                        <span className="text-primary text-12">ID</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">
                          DESCRIPTION
                        </span>
                      </th>
                      <th>
                        <span className="text-primary text-12">OPENING</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">DEBIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12">- CREDIT</span>
                      </th>
                      <th>
                        <span className="text-primary text-12 right">
                          CLOSING
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.ledgers.map((ledger) => (
                      <tr>
                        <td style={{ width: '200px' }}>{ledger.uniqueId}</td>
                        <td>{ledger.description}</td>
                        <td>{formatCurrency(ledger.opening)}</td>
                        <td>{formatCurrency(ledger.debit)}</td>
                        <td>{formatCurrency(ledger.credit)}</td>
                        <td>
                          <span className="right">
                            {formatCurrency(ledger.closing)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ExpTrialBalance>

            <ExpTrialBalance
              classNames="exp-primary"
              hideExpand
              headerData={{
                title: 'Grand Total',
                opening: data ? formatCurrency(data.opening) : 'N/A',
                closing: data ? formatCurrency(data.closing) : 'N/A',
                credit: data ? formatCurrency(data.credit) : 'N/A',
                debit: data ? `${formatCurrency(data.debit)}` : 'N/A',
              }}
            />
          </div>

          {data && (
            <div className="bx--row mt-35">
              <div className="bx--col-lg-2">
                <CustomButton
                  classes="secondary f-width font-bold"
                  onClick={exportTrialBalanceReport}>
                  <Upload size="11" strokeWidth="5px" />
                  &nbsp; EXPORT TO EXCEL
                </CustomButton>
              </div>
              <div className="bx--col-lg-2">
                <CustomButton
                  classes="primary f-width font-bold"
                  onClick={() => setOpenPrintModal(true)}>
                  <Printer size="11" strokeWidth="5px" /> &nbsp; PRINT
                </CustomButton>
              </div>
            </div>
          )}
        </div>

        <Footer fixed={false} showLinks={false} />
      </div>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  trialBalanceReport: selectTrialBalanceReport,
  currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTrialBalanceReport: (dateFrom, dateTo, authToken) =>
    dispatch({
      type: 'FETCH_TRIAL_BALANCE_REPORT_REQUESTED',
      payload: { dateFrom, dateTo, authToken },
    }),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TrialBalanceReportPage),
);
